import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { acceptInvite, previewInvite } from "../../api/auth";
import { ApiError } from "../../api/http";
import type { InvitePreview } from "../../api/types";
import { Button, PasswordInput, Label, Spinner } from "../../components/ui";
import { useT } from "../../i18n";
import { AuthLayout } from "./AuthLayout";

export function AcceptInvitePage() {
  const t = useT();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    previewInvite(token)
      .then((p) => {
        if (!cancelled) setPreview(p);
      })
      .catch((err) => {
        if (!cancelled) {
          setPreviewError(err instanceof ApiError ? err.message : t("common.error.generic"));
        }
      });
    return () => {
      cancelled = true;
    };
    // t is stable enough for this one-shot fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await acceptInvite(token, password);
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.error.generic"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title={t("auth.invite.title")}>
      {previewError ? (
        <p role="alert" className="text-sm text-danger">{previewError}</p>
      ) : done ? (
        <p role="status" className="text-sm text-success">{t("auth.reset.done")}</p>
      ) : !preview ? (
        <div className="grid place-items-center py-6">
          <Spinner />
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <p className="text-sm text-muted">
            {t("auth.invite.for")}{" "}
            <strong className="text-ink">{preview.email}</strong>
            {preview.school && <> · {preview.school}</>}
          </p>
          <div>
            <Label htmlFor="password">{t("auth.reset.password")}</Label>
            <PasswordInput
              id="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              showLabel={t("common.showPassword")}
              hideLabel={t("common.hidePassword")}
            />
          </div>
          {error && <p role="alert" className="text-sm text-danger">{error}</p>}
          <Button type="submit" loading={busy} className="w-full">
            {t("auth.invite.submit")}
          </Button>
        </form>
      )}
      <p className="mt-4 text-center">
        <Link to="/login" className="text-sm font-medium text-brand hover:underline">
          {t("auth.backToLogin")}
        </Link>
      </p>
    </AuthLayout>
  );
}
